/**
 * Interface principale de chat avec streaming SSE (EX-39).
 * Intègre SourcePanel + MiniGraph.
 */
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSSEStream } from "../../api/streaming";
import { useChatStore } from "../../store";
import MiniGraph from "../Graph/MiniGraph";
import SourcePanel from "./SourcePanel";

export default function ChatInterface() {
  const [input, setInput] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const messagesEndRef = useRef(null);

  const { messages, addMessage, activeConversationId, newConversation } = useChatStore();
  const { tokens, sources, graphData, isLoading, error, queryMode, fallbackUsed, startStream, stopStream } =
    useSSEStream();

  const conversationId = activeConversationId || newConversation();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [tokens, messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMsg = { role: "user", content: input, id: Date.now() };
    addMessage(userMsg);
    startStream(input, conversationId);
    setInput("");
  };

  return (
    <div className="chat-interface">
      {/* Zone de messages */}
      <div className="chat-messages">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}

        {/* Token streaming en cours */}
        {(isLoading || tokens) && (
          <div className="message message--assistant">
            <div className="message__content">
              <ReactMarkdown>{tokens || "…"}</ReactMarkdown>
              {isLoading && <span className="cursor-blink">▌</span>}
            </div>

            {/* Sources (EX-15) — affichées pendant/après le streaming */}
            {sources.length > 0 && (
              <SourcePanel
                sources={sources}
                queryMode={queryMode}
                fallbackUsed={fallbackUsed}
              />
            )}

            {/* Mini-graphe — uniquement pour relational/mixed (EX-15) */}
            {graphData && (queryMode === "relational" || queryMode === "mixed") && (
              <MiniGraph
                nodes={graphData.nodes}
                edges={graphData.edges}
                onNodeClick={setSelectedNode}
              />
            )}
          </div>
        )}

        {error && (
          <div className="message message--error">
            <p>⚠️ {error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Panneau latéral nœud sélectionné */}
      {selectedNode && (
        <div className="node-detail-panel">
          <h4>{selectedNode.label} <em>({selectedNode.type})</em></h4>
          <button onClick={() => setSelectedNode(null)}>✕</button>
        </div>
      )}

      {/* Zone de saisie */}
      <form className="chat-input" onSubmit={handleSubmit}>
        <textarea
          className="chat-input__textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Posez votre question…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          rows={2}
        />
        <div className="chat-input__actions">
          {isLoading ? (
            <button type="button" className="btn btn--danger" onClick={stopStream}>
              ⏹ Arrêter
            </button>
          ) : (
            <button type="submit" className="btn btn--primary" disabled={!input.trim()}>
              Envoyer ↵
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function MessageBubble({ message }) {
  return (
    <div className={`message message--${message.role}`}>
      <div className="message__content">
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </div>
      {message.sources?.length > 0 && (
        <SourcePanel
          sources={message.sources}
          queryMode={message.query_mode}
          fallbackUsed={message.fallback_used}
          groundingScore={message.grounding_score}
        />
      )}
      {message.graph_data && (message.query_mode === "relational" || message.query_mode === "mixed") && (
        <MiniGraph
          nodes={message.graph_data.nodes || []}
          edges={message.graph_data.edges || []}
        />
      )}
    </div>
  );
}
